import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  instantInterviewService,
  InstantInterviewRegistration,
  JobDescription,
} from '@/services/api';

interface InstantInterviewState {
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
  jobDescriptions: JobDescription[];
  linkUrl: string | null;
}

const initialState: InstantInterviewState = {
  isLoading: false,
  isSubmitting: false,
  error: null,
  success: false,
  jobDescriptions: [],
  linkUrl: null,
};

interface RegisterResponse {
  statusCode: number;
  message: string;
  data: string;
}

export const registerInstantInterview = createAsyncThunk<
  RegisterResponse,
  InstantInterviewRegistration,
  { rejectValue: string }
>(
  'instantInterview/register',
  async (data: InstantInterviewRegistration, { rejectWithValue }) => {
    try {
      const response = (await instantInterviewService.register(
        data
      )) as RegisterResponse;
      return response;
    } catch (error: unknown) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to register for interview'
      );
    }
  }
);

export const fetchJobDescriptions = createAsyncThunk<
  JobDescription[],
  void,
  { rejectValue: string }
>(
  'instantInterview/fetchJobDescriptions',
  async (_, { rejectWithValue }) => {
    try {
      const data = await instantInterviewService.getJobDescriptions();
      return data;
    } catch (error: unknown) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : 'Failed to fetch job descriptions'
      );
    }
  }
);

const instantInterviewSlice = createSlice({
  name: 'instantInterview',
  initialState,
  reducers: {
    resetInstantInterviewState: state => {
      state.isSubmitting = false;
      state.error = null;
      state.success = false;
      state.linkUrl = null;
    },
  },
  extraReducers: builder => {
    builder
      // Register
      .addCase(registerInstantInterview.pending, state => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(registerInstantInterview.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.success = true;
        state.linkUrl = action.payload.data;
      })
      .addCase(registerInstantInterview.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || 'Registration failed';
      })
      // Job Descriptions
      .addCase(fetchJobDescriptions.pending, state => {
        state.isLoading = true;
      })
      .addCase(
        fetchJobDescriptions.fulfilled,
        (state, action: PayloadAction<JobDescription[]>) => {
          state.isLoading = false;
          state.jobDescriptions = action.payload;
        }
      )
      .addCase(fetchJobDescriptions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch job descriptions';
      });
  },
});

export const { resetInstantInterviewState } = instantInterviewSlice.actions;
export default instantInterviewSlice.reducer;
